import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from 'locale';

import { formatDateWithoutLocalConversion } from 'utils/datetime';
import { DEFAULT_APP_CURRENCY } from 'constants/constants';
import { ApplicationTypesEnum } from 'components/ApplicationDetails/Applications/Applications.types';
import './ProductSummaryItem.css';

type WithdrawalType = {
    amount: number;
    frequency: number;
    start: string;
    iban: string;
    holder: string | Array<string>;
    bank: string;
    plannedEndDate: string;
}

type ProductSummaryItemProps = {
    oneTimeInvestment: number;
    periodicPayment: number;
    frequency: number;
    transfer: number;
    category: string;
    strategy: string;
    pensionSchema: string;
    isPk: boolean;
    isZic: boolean;
    is3a: boolean;
    is3ap: boolean;
    isFz: boolean;
    isAZP: boolean;
    isFullTransfer: boolean;
    // eslint-disable-next-line react/require-default-props
    withdrawal?: Partial<WithdrawalType>
    applicationType: string;
    depotNr?: string;
    productName?: string;
    oldStrategy?: string;
    pbNumber: string;
    isCEP?: boolean;
    currentInvestment?: number;
    amountToBeBought?: number;
    amountToBeSold?: number,
    issueCommission?: number,
    entryCostFee?: number
}

const ProductSummaryItem: FC<ProductSummaryItemProps> = ({
    oneTimeInvestment,
    periodicPayment,
    strategy,
    category,
    transfer,
    frequency,
    pensionSchema,
    isPk,
    isZic,
    is3a,
    is3ap,
    isFz,
    isFullTransfer,
    withdrawal,
    applicationType = ApplicationTypesEnum.NEW,
    depotNr,
    productName,
    oldStrategy,
    isCEP,
    pbNumber,
    isAZP,
    currentInvestment,
    amountToBeBought,
    amountToBeSold,
    issueCommission,
    entryCostFee,
}) => {
    const { t } = useTranslation();
    const { getAmountWithDecimals, getFormattedCurrency } = useFormatting();

    const isChangeApp = applicationType === ApplicationTypesEnum.CHANGE;

    return (
        <section className="product-summary-item">
            <p className="title">
                <span>{t(applicationType)}</span>
                {productName}
            </p>

            {depotNr && (
                <div className="data-row">
                    <p>{t('ApplicationDetails.AdvisorySection.ProductApplications.DepotNumber')}</p>
                    <p>
                        {depotNr}
                    </p>
                </div>
            )}

            {isChangeApp && (
                <>
                    <div className="data-row">
                        <p>{t('ApplicationDetails.AdvisorySection.ProductApplications.CurrentStrategy')}</p>
                        <p>
                            {oldStrategy}
                        </p>
                    </div>
                    <div className="data-row">
                        <p>{t('ShoppingCart.Modal.Form.Label.NewStrategy')}</p>
                        <p>
                            {strategy}
                        </p>
                    </div>
                </>
            )}

            {isPk || (
                <div className="data">
                    {(is3a || is3ap) && (
                        <>
                            <div className="data-row">
                                <p>{t('portfolio.createNewPortfolio.overview.yearlyAmount')}</p>
                                <p>
                                    {getAmountWithDecimals(transfer)}
                                </p>
                            </div>
                            <div className="data-row">
                                <p>{t('clients.portfolioNav.transfer')}</p>
                                <p>
                                    {getAmountWithDecimals(oneTimeInvestment)}
                                </p>
                            </div>
                        </>
                    )}
                    {isFz && (
                        <>
                            <div className="data-row">
                                <p>{t('3b.withdrawalPlan.transferAmount')}</p>
                                <p>
                                    {getAmountWithDecimals(oneTimeInvestment)}
                                </p>
                            </div>
                            <div className="data-row">
                                <p>{t('productSummary.transferType')}</p>
                                <p>{t(isFullTransfer ? 'portfolio.createNewPortfolio.configuration.options.fullTransfer' : 'portfolio.createNewPortfolio.configuration.options.partialTransfer')}</p>
                            </div>
                            <div className="data-row">
                                <p>{t('portfolio.createNewPortfolio.previousPensionScheme')}</p>
                                <p>{pensionSchema}</p>
                            </div>
                        </>
                    )}
                    {is3a || is3ap || isFz || isCEP || (
                        <div className="data-row">
                            <p>{t('portfolio.createNewPortfolio.oneTimeInvestment')}</p>
                            <p>
                                {getAmountWithDecimals(oneTimeInvestment)}
                            </p>
                        </div>
                    )}
                    {is3a || is3ap || isFz || (
                        <>
                            {isZic || isCEP || isAZP || (
                                <div className="data-row">
                                    <p>{t('portfolio.createNewPortfolio.periodicPayment')}</p>
                                    <p>
                                        {periodicPayment ? getAmountWithDecimals(periodicPayment) : '-'}
                                    </p>
                                </div>
                            )}
                            {isZic || isCEP || isAZP || (
                                <div className="data-row">
                                    <p>{t('productSummary.frequency')}</p>
                                    <p>{periodicPayment ? frequency : '-'}</p>
                                </div>
                            )}
                            {isCEP || (
                                <div className="data-row">
                                    <p>{t('clientDashboard.documents.category')}</p>
                                    <p>{category ?? '-'}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {isCEP && !isChangeApp && (
                <div className="data-row">
                    <p>{t('ShoppingCart.ProductRow.CurrentAmount')}</p>
                    <p>{getAmountWithDecimals(currentInvestment || 0)}</p>
                </div>
            )}

            {amountToBeBought ? (
                <div className="data-row">
                    <p>{t('ApplicationDetails.AdvisorySection.ProductApplications.PurchaseAmount')}</p>
                    <p>
                        +
                        {' '}
                        {getAmountWithDecimals(amountToBeBought)}
                    </p>
                </div>
            ) : ''}

            {amountToBeSold && (
                <div className="data-row">
                    <p>{t('ApplicationDetails.AdvisorySection.ProductApplications.ToBeSold')}</p>
                    <p>
                        -
                        {' '}
                        {getAmountWithDecimals(amountToBeSold)}
                    </p>
                </div>
            )}

            {!isCEP && !isZic && !isPk && issueCommission != null && (
                <div className="data-row">
                    <p>{t('portfolio.createNewPortfolio.agentInformation.issueCommission')}</p>
                    <p>
                        {issueCommission?.toFixed(1)}
                        %
                    </p>
                </div>
            )}

            {isZic && entryCostFee != null && (
                <div className="data-row">
                    <p>{t('entryCostFee.labels.entryCost')}</p>
                    <p>
                        {entryCostFee?.toFixed(1)}
                        %
                    </p>
                </div>
            )}

            {pbNumber && (
                <div className="data-row">
                    <p>PB-Nr</p>
                    <p>{pbNumber}</p>
                </div>
            )}

            {withdrawal && (
                <>
                    <h3>{t('portfolio.createNewPortfolio.withdrawalPlan')}</h3>
                    <div className="data-row">
                        <p>{t('clientDashboard.portfolio.columns.amount')}</p>
                        <p>
                            {`${getFormattedCurrency(withdrawal.amount, {
                                currency: DEFAULT_APP_CURRENCY,
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                            })}`}
                        </p>
                    </div>
                    <div className="data-row">
                        <p>{t('productSummary.frequency')}</p>
                        <p>{withdrawal.frequency}</p>
                    </div>
                    <div className="data-row">
                        <p>{t('surfly.tabStart')}</p>
                        <p>{formatDateWithoutLocalConversion(withdrawal.start, 'MM.YYYY')}</p>
                    </div>
                    {withdrawal.plannedEndDate && (
                        <div className="data-row">
                            <p>
                                {t('3b.withdrawalPlan.plannedEndDate')
                                    .replaceAll('*', '')}
                            </p>
                            <p>{formatDateWithoutLocalConversion(withdrawal.plannedEndDate, 'MM.YYYY')}</p>
                        </div>
                    )}
                    <div className="data-row">
                        <p>IBAN</p>
                        <p>{withdrawal.iban}</p>
                    </div>
                    <div className="data-row">
                        <p>{t('3b.withdrawalPlan.accountHolder')}</p>
                        <p>{withdrawal.holder}</p>
                    </div>
                    <div className="data-row">
                        <p>{t('onBoarding.bank')}</p>
                        <p>{withdrawal.bank}</p>
                    </div>
                </>
            )}
        </section>
    );
};

export default ProductSummaryItem;
